import { highResVideoDimensions } from '../Config/videoInput'

const createVideoElementForStream = (stream, dimensions) => {
  const videoElement = document.createElement('video')
  videoElement.style.height = dimensions.height
  videoElement.style.width = dimensions.width
  videoElement.style['object-fit'] = 'cover'
  videoElement.srcObject = stream
  return videoElement
}

const createBaseCanvas = ({ height, width }) => {
  const canvasElement = document.createElement('canvas')

  canvasElement.height = height
  canvasElement.width = width

  return canvasElement
}

const getVideoStream = (deviceId, aspectRatio) =>
  deviceId === 'screen'
    ? navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          logicalSurface: true,
        },
        audio: false,
      })
    : navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          aspectRatio: { exact: aspectRatio },
          width: {
            max: highResVideoDimensions.width,
            ideal: highResVideoDimensions.width,
          },
          height: {
            max: highResVideoDimensions.height,
            ideal: highResVideoDimensions.height,
          },
        },
      })

const drawFrameGenerator = () => {
  let _stopped = false

  const stop = () => {
    _stopped = true
  }

  const draw = (
    context,
    videoElement,
    dimensions,
    screenVideoElement = null,
  ) => {
    const sf = 0.25
    const marginY = 0.025
    const marginX = 0.0125

    if (screenVideoElement) {
      context.drawImage(
        screenVideoElement,
        0,
        0,
        dimensions.width,
        dimensions.height,
      )
      context.drawImage(
        videoElement,
        dimensions.width * (1 - sf - marginX),
        dimensions.height * marginY,
        dimensions.width * sf,
        dimensions.height * sf,
      )
    } else {
      context.drawImage(videoElement, 0, 0, dimensions.width, dimensions.height)
    }

    if (!_stopped) {
      requestAnimationFrame(() =>
        draw(context, videoElement, dimensions, screenVideoElement),
      )
    } else {
      context = null
    }
  }

  return { stop, draw }
}

export const getVideoTrack = async (
  deviceId,
  dimensions,
  withScreen = false,
) => {
  try {
    const stream = await getVideoStream(
      deviceId,
      dimensions.width / dimensions.height,
      dimensions,
    )

    const screenStream = await (withScreen
      ? getVideoStream(
          'screen',
          dimensions.width / dimensions.height,
          dimensions,
        )
      : null)

    const canvasElement = createBaseCanvas(dimensions)
    const context = canvasElement.getContext('2d', {
      alpha: false,
      desynchronized: true,
    })

    const videoElement = createVideoElementForStream(stream, dimensions)
    const screenVideoElement = withScreen
      ? createVideoElementForStream(screenStream, dimensions)
      : null

    await videoElement.play()
    if (screenStream) {
      await screenVideoElement.play()
    }

    let { draw, stop: stopDrawing } = drawFrameGenerator()

    requestAnimationFrame(() =>
      draw(context, videoElement, dimensions, screenVideoElement),
    )

    let track = canvasElement.captureStream(30).getVideoTracks()[0]

    let stop = (stopCanvas) => {
      stream.getTracks().forEach((track) => track.stop())
      if (withScreen) {
        screenStream.getTracks().forEach((track) => track.stop())
      }
      stopDrawing()

      if (stopCanvas) {
        track.stop()
      }
    }

    const switchCamera = async (deviceId, withScreen) => {
      const stream = await getVideoStream(
        deviceId,
        dimensions.width / dimensions.height,
      )

      const screenStream = await (withScreen
        ? getVideoStream(
            'screen',
            dimensions.width / dimensions.height,
            dimensions,
          )
        : null)

      const videoElement = createVideoElementForStream(stream, dimensions)
      const screenVideoElement = withScreen
        ? createVideoElementForStream(screenStream, dimensions)
        : null

      await videoElement.play()
      if (screenStream) {
        await screenVideoElement.play()
      }

      const drawFrame = drawFrameGenerator()

      stop()

      stop = (stopCanvas) => {
        stream.getTracks().forEach((track) => track.stop())
        if (withScreen) {
          screenStream.getTracks().forEach((track) => track.stop())
        }
        drawFrame.stop()

        if (stopCanvas) {
          track.stop()
        }
      }

      requestAnimationFrame(() =>
        drawFrame.draw(context, videoElement, dimensions, screenVideoElement),
      )

      return stop
    }

    return [track, stop, switchCamera]
  } catch (e) {
    console.error('this', e)
    return []
  }
}
